import { CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react';
import type { UserStatus } from '@/types/admin';

export const getStatusIcon = (status: UserStatus) => {
  switch (status.toLowerCase()) {
    case 'thriving':
      return CheckCircle;
    case 'managing':
      return AlertTriangle;
    case 'struggling':
      return XCircle;
    case 'inactive':
      return Clock;
    default:
      return Clock;
  }
};

export const getStatusBadgeColor = (status: UserStatus): string => {
  switch (status.toLowerCase()) {
    case 'thriving':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'managing':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'struggling':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'inactive':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getStatusTooltip = (status: UserStatus): string => {
  switch (status.toLowerCase()) {
    case 'thriving':
      return 'Actively engaged with the platform. Coaching themes reflect general growth and self-improvement areas. Consistently completes activities.';
    case 'managing':
      return 'Engagement is patchy. Coaching themes suggest deeper challenges emerging. Inconsistent in completing activities.';
    case 'struggling':
      return 'Low engagement. Coaching themes point to significant concerns (e.g., burnout, emotional distress). Rarely completes activities.';
    case 'inactive':
      return 'No recent activity. User has not engaged with the platform for an extended period. Consider re-engagement strategies.';
    default:
      return 'Status information not available.';
  }
};

export const getThemeColor = (_index: number): string => {
  return 'bg-primary';
};

export const getThemeColorLight = (_index: number): string => {
  return 'bg-primary/10 text-primary';
};

export const formatUserInitials = (name: string): string => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

export const sortUsersByProperty = <T>(
  users: T[], 
  property: keyof T, 
  direction: 'asc' | 'desc' = 'desc'
): T[] => {
  return [...users].sort((a, b) => {
    const aVal = a[property];
    const bVal = b[property];
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'desc' ? bVal - aVal : aVal - bVal;
    }
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'desc' 
        ? bVal.localeCompare(aVal)
        : aVal.localeCompare(bVal);
    }
    
    return 0;
  });
};